import { displayName } from '../../types';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { Add, GetApp, Visibility, PlaylistAddCheck, Close } from '@mui/icons-material';
import { apiService } from '../../services/api';
import CreatePayrollRunModal from '../../components/CreatePayrollRunModal';
import PayrollChecklist from './PayrollChecklist';

const Payroll: React.FC = () => {
  const [payrollRuns, setPayrollRuns] = useState<any[]>([]);
  const [adjustments, setAdjustments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPayrollModalOpen, setIsPayrollModalOpen] = useState(false);

  // Checklist dialog
  const [checklistRun, setChecklistRun] = useState<{ id: string; periodName: string } | null>(null);

  const fetchPayrollData = async () => {
    try {
      setLoading(true);
      const [runs, adjs] = await Promise.all([
        apiService.getPayrollRuns(),
        apiService.getPayrollAdjustments(),
      ]);
      setPayrollRuns(runs);
      setAdjustments(adjs);
    } catch (err: any) {
      console.error('Error fetching payroll data:', err);
      setError('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollData();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'locked':
        return 'success';
      case 'reviewing':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent={{ xs: "column", sm: "space-between" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={3}>
        <Typography variant="h4">Payroll Management</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setIsPayrollModalOpen(true)}
        >
          Create Payroll Run
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Total Runs</Typography>
              <Typography variant="h4" color="primary">{payrollRuns.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Pending Adjustments</Typography>
              <Typography variant="h4" color="warning.main">
                {adjustments.filter(adj => adj.status === 'pending').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>This Month Total</Typography>
              <Typography variant="h4" color="success.main">
                {payrollRuns.length > 0 ? '$' + (payrollRuns[0]?.totalNetPay || 0).toLocaleString() : '$0'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Employees Paid</Typography>
              <Typography variant="h4" color="info.main">
                {payrollRuns.length > 0 ? payrollRuns[0]?.payrollItems?.length || 0 : 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Payroll Runs */}
      <Typography variant="h6" gutterBottom>Payroll Runs</Typography>
      <Paper sx={{ mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Pay Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Gross Pay</TableCell>
                <TableCell>Net Pay</TableCell>
                <TableCell>Approved By</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payrollRuns.map((run) => (
                <TableRow key={run.id} hover>
                  <TableCell>{run.periodName}</TableCell>
                  <TableCell>{run.entity?.name}</TableCell>
                  <TableCell>{new Date(run.payDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} alignItems="center">
                      <Chip
                        label={run.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(run.status)}
                        size="small"
                      />
                      {run.currency && (
                        <Chip label={run.currency} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{run.currency || ''} {run.totalGrossPay?.toLocaleString() || '0'}</TableCell>
                  <TableCell>{run.currency || ''} {run.totalNetPay?.toLocaleString() || '0'}</TableCell>
                  <TableCell>{run.approvedBy?.fullName || '-'}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        disabled
                        title="Payroll run detail view is not yet available"
                      >
                        View (soon)
                      </Button>
                      <Button
                        size="small"
                        startIcon={<PlaylistAddCheck />}
                        color="secondary"
                        onClick={() => setChecklistRun({ id: run.id, periodName: run.periodName })}
                      >
                        Checklist
                      </Button>
                      {run.status === 'approved' && (
                        <Button
                          size="small"
                          startIcon={<GetApp />}
                          disabled
                          title="Payroll export is not yet available"
                        >
                          Export (soon)
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {payrollRuns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No payroll runs found. Create your first payroll run to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Recent Adjustments */}
      <Typography variant="h6" gutterBottom>Recent Adjustments</Typography>
      <Paper sx={{ overflowX: "auto" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adjustments.slice(0, 5).map((adjustment) => (
                <TableRow key={adjustment.id} hover>
                  <TableCell>{displayName(adjustment.employee)}</TableCell>
                  <TableCell>{adjustment.itemType?.replace('_', ' ')}</TableCell>
                  <TableCell>${adjustment.amount?.toLocaleString()} {adjustment.currency}</TableCell>
                  <TableCell>{adjustment.reason}</TableCell>
                  <TableCell>
                    <Chip
                      label={adjustment.status.toUpperCase()}
                      color={getStatusColor(adjustment.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{displayName(adjustment.createdBy)}</TableCell>
                  <TableCell>{new Date(adjustment.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {adjustments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">No adjustments found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Payroll Close Checklist Dialog */}
      <Dialog
        open={!!checklistRun}
        onClose={() => setChecklistRun(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <PlaylistAddCheck color="secondary" />
              <span>Payroll Close Checklist</span>
            </Box>
            <IconButton onClick={() => setChecklistRun(null)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          {checklistRun && (
            <PayrollChecklist
              payrollRunId={checklistRun.id}
              periodName={checklistRun.periodName}
            />
          )}
        </DialogContent>
      </Dialog>

      <CreatePayrollRunModal
        open={isPayrollModalOpen}
        onClose={() => setIsPayrollModalOpen(false)}
        onPayrollRunCreated={() => { fetchPayrollData(); }}
      />
    </Box>
  );
};

export default Payroll;
